import { parseBoldSegments } from "./parseBoldMarkers";

// Shared plumbing for every contentEditable-based AI-text editor in the
// care plan (FreeTextSection, the symptom name row) - built to replace an
// earlier invisible-textarea + styled-backdrop-overlay trick. That trick
// could never truly reconcile "looks like real bold" with "cursor stays in
// the right spot": a plain <textarea> only ever renders ONE uniform
// font-weight for its whole value, so real bold text (wider than the same
// characters at normal weight) always rendered wider in the backdrop than
// in the invisible textarea underneath, drifting the visible caret out of
// alignment the moment there was any bold content before it. A
// contentEditable element has only one rendered layer - real <strong>
// elements at real bold weight, real width - so the browser's own native
// cursor always tracks the actual rendered text correctly, no matter how
// much bold content precedes it.
//
// "**" markers and "\n" line breaks never exist as literal DOM text here -
// bold is real <strong> elements, line breaks are real <br> elements - and
// both are reconstructed into the raw "**text**"/"\n" storage string fresh
// on every edit (contentEditableToMarkerString). That also means the
// earlier class of bug where a Backspace could corrupt a raw "**" marker
// into a broken, orphaned single asterisk is structurally impossible here,
// not just guarded against - there's no literal asterisk character for a
// Backspace to ever land on.

// Reconstructs the raw "**bold**"/"\n" storage string from a
// contentEditable's DOM content (or a cloned fragment of one, e.g. from
// Range.cloneContents()).
//
// Bug fix: inserting a node (e.g. a <br>, via insertBreakAtCaret below)
// with the caret positioned mid-way through a <strong>'s text splits that
// text node and inserts the new node as a SIBLING of both halves *inside*
// the <strong>, per the DOM Range spec - e.g. "**Redskin**" with a break
// after "Red" becomes <strong>Red<br>skin</strong>, not two separate
// <strong> siblings. Treating STRONG/B as an opaque leaf (just its
// .textContent, which is empty for a <br>) silently dropped the break
// entirely. This instead recurses into STRONG/B, wrapping each of its
// text runs in its own "**...**" pair and emitting a real "\n" for any
// <br> found inside - "**Red**\n**skin**" round-trips correctly and
// renders back out (renderMarkerStringIntoContentEditable) as the
// canonical two-sibling-<strong> structure on the next external sync.
// A <strong>/<b> whose text content is empty is skipped entirely rather
// than emitting a pointless orphaned "**" that parseBoldSegments (requires
// 1+ content characters) could never recognize as bold again.
export function contentEditableToMarkerString(root) {
  let result = "";
  const walk = (node, insideBold) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!node.textContent) return;
      result += insideBold ? `**${node.textContent}**` : node.textContent;
    } else if (node.nodeName === "STRONG" || node.nodeName === "B") {
      node.childNodes.forEach((child) => walk(child, true));
    } else if (node.nodeName === "BR") {
      result += "\n";
    } else {
      node.childNodes.forEach((child) => walk(child, insideBold));
    }
  };
  root.childNodes.forEach((node) => walk(node, false));
  return result;
}

// Populates a contentEditable element's DOM from a raw "**bold**"/"\n"
// storage string - real <strong> elements for bold segments, a real <br>
// for every "\n".
export function renderMarkerStringIntoContentEditable(el, value) {
  el.innerHTML = "";
  parseBoldSegments(value || "").forEach((segment) => {
    const lines = segment.text.split("\n");
    lines.forEach((line, i) => {
      if (i > 0) el.appendChild(document.createElement("br"));
      if (!line) return;
      if (segment.bold) {
        const strong = document.createElement("strong");
        strong.style.fontWeight = "700";
        strong.style.color = "#6b7280";
        strong.textContent = line;
        el.appendChild(strong);
      } else {
        el.appendChild(document.createTextNode(line));
      }
    });
  });
}

// Places the caret at a given offset into the element's rendered (display)
// text, where each <br> counts as exactly one character (matching "\n" in
// the raw string) - the same coordinate space window.getSelection() itself
// uses, which has no "**" characters in it since those are never real DOM
// text.
export function setCaretAtDisplayOffset(container, offset) {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  let remaining = offset;
  let placed = false;
  const walk = (node) => {
    if (placed) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent.length;
      if (remaining <= len) {
        range.setStart(node, remaining);
        range.collapse(true);
        placed = true;
      } else {
        remaining -= len;
      }
    } else if (node.nodeName === "BR") {
      if (remaining <= 0) {
        range.setStartBefore(node);
        range.collapse(true);
        placed = true;
      } else {
        remaining -= 1;
      }
    } else {
      node.childNodes.forEach(walk);
    }
  };
  container.childNodes.forEach(walk);
  if (!placed) {
    range.selectNodeContents(container);
    range.collapse(false);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

// The plain-text (display) length of a raw "**marker**" string - its
// length with the marker characters themselves excluded (a "\n" still
// counts as one character, matching a <br>), matching the coordinate space
// setCaretAtDisplayOffset/window.getSelection() use.
export function markerStringDisplayLength(rawValue) {
  return parseBoldSegments(rawValue || "").reduce((len, segment) => len + segment.text.length, 0);
}

// Inserts a real <br> at the current caret position (splitting a text node
// or a <strong> there as needed, same as any other DOM Range.insertNode)
// and leaves the caret positioned right after it - used for Enter in
// multi-line editors instead of relying on the browser's own inconsistent
// default contentEditable line-break behavior (which inserts wrapping
// <div>s in some browsers, <br> in others).
export function insertBreakAtCaret() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const br = document.createElement("br");
  range.insertNode(br);
  range.setStartAfter(br);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

const isBoldNode = (node) => node && (node.nodeName === "STRONG" || node.nodeName === "B");

// The nearest <strong>/<b> containing `node`, stopping at the editor root.
const closestBoldWithin = (node, root) => {
  for (let n = node; n && n !== root; n = n.parentNode) {
    if (isBoldNode(n)) return n;
  }
  return null;
};

// SCRUM-788 bug fix: only AI-added text is bold. With the caret at the end
// of an AI "**...**" run (or anywhere inside one), the browser's native
// typing lands inside that same <strong> - or, in Chrome, carries the bold
// "typing style" over from the preceding character - so whatever the user
// typed was saved inside the AI's markers ("**AI text(added
// 10/01/2026) user typing**"): rendered bold, and the date tag no longer
// sat at the end of the bold text where parseSegmentsWithDateTags looks for
// it. Call from a native "beforeinput" listener (React 17's onBeforeInput
// has no inputType): when a typed character would end up bold,
// this cancels the native insert and places it as a plain text node
// instead, splitting the <strong> at the caret if needed. Returns true when
// it handled the insert (the caller must then sync state itself, since no
// input event follows a cancelled beforeinput). Everything else - typing
// in plain text, IME composition, paste, deletes - is left to the browser,
// so native undo is untouched there; and once the caret sits in the new
// plain text node, the following keystrokes take the native path too.
export function insertTypedTextAsPlain(e, root) {
  if (e.inputType !== "insertText" || !e.data || e.isComposing) return false;
  const sel = window.getSelection();
  if (!root || !sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return false;

  const { startContainer, startOffset } = range;
  const insideBold = closestBoldWithin(startContainer, root);
  const precededByBold =
    startContainer.nodeType === Node.ELEMENT_NODE && isBoldNode(startContainer.childNodes[startOffset - 1]);
  if (!insideBold && !precededByBold) return false;

  e.preventDefault();
  range.deleteContents();
  const textNode = document.createTextNode(e.data);
  // deleteContents can move the caret, so look up the enclosing bold again.
  const bold = closestBoldWithin(range.startContainer, root);
  if (bold) {
    // Move everything after the caret out of the <strong> (extractContents
    // clones the partially-selected <strong> around it), then put the typed
    // text between the two halves.
    const tailRange = document.createRange();
    tailRange.setStart(range.startContainer, range.startOffset);
    tailRange.setEndAfter(bold);
    const tail = tailRange.extractContents();
    bold.parentNode.insertBefore(textNode, bold.nextSibling);
    if (tail.textContent || tail.querySelector("br")) {
      textNode.parentNode.insertBefore(tail, textNode.nextSibling);
    }
    if (!bold.textContent && !bold.querySelector("br")) bold.remove();
  } else {
    range.insertNode(textNode);
  }

  const caret = document.createRange();
  caret.setStart(textNode, textNode.length);
  caret.collapse(true);
  sel.removeAllRanges();
  sel.addRange(caret);
  return true;
}
